import { Badge } from '@/components/ui/badge';
import {
  Box,
  Shield,
  AlertTriangle,
  Building2,
  Network,
  Server,
  FileText,
  AlertCircle,
  CheckSquare,
  Briefcase,
  type LucideIcon,
} from 'lucide-react';
import { EntityType } from '@/types/link';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface RelationshipBadgeProps {
  entityType: EntityType;
  entityId: string;
  entityName: string;
  metadata?: Record<string, unknown>;
  onClick?: () => void;
}

const entityConfig: Record<EntityType, { icon: LucideIcon; color: string; route: string }> = {
  asset: { icon: Box, color: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', route: '/assets' },
  site: { icon: Building2, color: 'bg-purple-500/10 text-purple-700 dark:text-purple-400', route: '/sites' },
  zone: { icon: Network, color: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400', route: '/sites' },
  system: { icon: Server, color: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400', route: '/assets' },
  risk: { icon: AlertTriangle, color: 'bg-red-500/10 text-red-700 dark:text-red-400', route: '/risks' },
  control: { icon: Shield, color: 'bg-green-500/10 text-green-700 dark:text-green-400', route: '/controls' },
  supplier: { icon: Briefcase, color: 'bg-orange-500/10 text-orange-700 dark:text-orange-400', route: '/suppliers' },
  incident: { icon: AlertCircle, color: 'bg-red-500/10 text-red-700 dark:text-red-400', route: '/incidents' },
  task: { icon: CheckSquare, color: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', route: '/assets' },
  evidence: { icon: FileText, color: 'bg-gray-500/10 text-gray-700 dark:text-gray-400', route: '/evidence' },
  shared_infrastructure: { icon: Network, color: 'bg-teal-500/10 text-teal-700 dark:text-teal-400', route: '/sites' },
};

export function RelationshipBadge({ 
  entityType, 
  entityName, 
  metadata,
  onClick 
}: RelationshipBadgeProps) {
  const navigate = useNavigate();
  const config = entityConfig[entityType];
  const Icon = config.icon;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(config.route);
    }
  };

  const tooltipContent = (
    <div className="space-y-1">
      <p className="font-medium">{entityName}</p>
      <p className="text-xs capitalize">{entityType.replace('_', ' ')}</p>
      {metadata && Object.entries(metadata).map(([key, value]) => (
        <p key={key} className="text-xs">
          <span className="opacity-70">{key}:</span> {String(value)}
        </p>
      ))}
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`${config.color} cursor-pointer hover:opacity-80 transition-opacity`}
            onClick={handleClick}
          >
            <Icon className="w-3 h-3 mr-1" />
            {entityName}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
